import { apiClient as api } from './apiClient';

// ═══════════════════════════════════════════════════════════════
// Módulo Empleados (O-01, O-02, O-03) — contrato del backend (EmpleadosController,
// api/empleados). Tipos de CONTRATO.md §2 (campos profesionales), §4 (API) y §8 (listado
// ampliado); recursos de §6. Lecturas (listado paginado, contadores, ficha, catálogos) y
// escrituras (alta, edición, roles, baja). Nada de nombres ni emails en la URL: el texto
// buscado va por POST.
// ═══════════════════════════════════════════════════════════════

export const RECURSO_EMPLEADOS_VER = 'Empleados.Ver';
export const RECURSO_EMPLEADOS_GESTIONAR = 'Empleados.Gestionar';

/** Estado que se pide al listado; el servidor entiende «activos» si no se indica. */
export type EstadoListado = 'activos' | 'bajas' | 'todos';

/** Campos por los que el servidor sabe ordenar (§8); cualquier otro da 400. */
export type CampoOrdenEmpleados = 'nombre' | 'email' | 'puesto' | 'delegacion' | 'zona' | 'alta' | 'estado';
export type DireccionOrden = 'asc' | 'desc';

export interface OrdenEmpleados {
  campo: CampoOrdenEmpleados;
  direccion: DireccionOrden;
}

/** Cuerpo de `POST api/empleados/consulta` (§8). Todo es opcional; `pagina` empieza en 1 y `tamano` va de 10 a 100. */
export interface EmpleadoConsulta {
  /** Nombre, email o código corto. */
  texto?: string;
  estado?: EstadoListado;
  idDelegacion?: number;
  idZona?: number;
  puesto?: string;
  departamento?: string;
  orden?: OrdenEmpleados;
  pagina?: number;
  tamano?: number;
}

/** Una página del listado: `total` es el de todo el filtro, no el de la página. */
export interface EmpleadosPagina {
  elementos: EmpleadoResumen[];
  total: number;
  pagina: number;
  tamano: number;
}

/** Fila del listado (FC_pr_EMPLEADO_LISTADO_READ). */
export interface EmpleadoResumen {
  idEmpleado: number;
  codigoCorto?: string | null;
  nombreCompleto: string;
  emailEmpresarial?: string | null;
  puesto?: string | null;
  departamento?: string | null;
  idDelegacion?: number | null;
  delegacion?: string | null;
  idZona?: number | null;
  zona?: string | null;
  activo: boolean;
  /** yyyy-MM-dd */
  fechaAlta?: string | null;
  fechaBaja?: string | null;
}

export interface HistorialDelegacion {
  idDelegacion: number;
  delegacion?: string | null;
  desde?: string | null;
  /** Sin valor: la fila sigue abierta. */
  hasta?: string | null;
}

export interface HistorialZona {
  idZona: number;
  zona?: string | null;
  desde?: string | null;
  hasta?: string | null;
}

/** Ficha: los campos de §2 más los dos históricos (FC_pr_EMPLEADO_FICHA_READ). */
export interface EmpleadoFicha {
  idEmpleado: number;
  codigoCorto?: string | null;
  nombreCompleto: string;
  nombreAbreviado?: string | null;
  puesto?: string | null;
  departamento?: string | null;
  telefono?: string | null;
  emailEmpresarial?: string | null;
  idDelegacion?: number | null;
  idZona?: number | null;
  nivelSeguridad?: number | null;
  nivelAsesor?: number | null;
  nivelInterlocucion?: number | null;
  accesoGdgo: boolean;
  asignarCcl: boolean;
  controlAcompaniamiento: boolean;
  fechaAlta?: string | null;
  activo: boolean;
  fechaBaja?: string | null;
  /** GUID_ROL; su significado está por confirmar (Q-EMP-02). */
  roles: string[];
  historialDelegaciones: HistorialDelegacion[];
  historialZonas: HistorialZona[];
}

export const consultarEmpleados = async (q: EmpleadoConsulta): Promise<EmpleadosPagina> =>
  (await api.post<EmpleadosPagina>('/api/empleados/consulta', q)).data;

// ───────────────────────────── Contadores (GET api/empleados/contadores)

/** Las filas de los empleados sin delegación (o con una fuera del catálogo) traen `idDelegacion` o `delegacion` nulos (§8). */
export interface ContadorDelegacion {
  idDelegacion: number | null;
  delegacion: string | null;
  activos: number;
  bajas: number;
}

/** Panorama completo, sin filtros y sin datos personales (§8). */
export interface EmpleadosContadores {
  total: number;
  activos: number;
  bajas: number;
  porDelegacion: ContadorDelegacion[];
}

export const leerContadoresEmpleados = async (): Promise<EmpleadosContadores> =>
  (await api.get<EmpleadosContadores>('/api/empleados/contadores')).data;

export const leerFichaEmpleado = async (id: number): Promise<EmpleadoFicha> =>
  (await api.get<EmpleadoFicha>(`/api/empleados/${id}`)).data;

// ───────────────────────────── Catálogos (GET api/empleados/catalogos)

export interface DelegacionCatalogo { idDelegacion: number; descripcion: string }
export interface ZonaCatalogo { idZona: number; descripcion: string; idDelegacion?: number | null }
/**
 * Rol por su GUID y número de empleados que lo tienen. `nombre` es el del grupo de Entra con ese id
 * (varios, unidos con « / »), o `null` si el id no está en el mapa de roles (D-EMP-17).
 */
export interface RolCatalogo { guidRol: string; empleados: number; nombre?: string | null }
export interface ResponsableCatalogo { idEmpleado: number; nombreCompleto: string }

export interface EmpleadosCatalogos {
  delegaciones: DelegacionCatalogo[];
  zonas: ZonaCatalogo[];
  roles: RolCatalogo[];
  responsables: ResponsableCatalogo[];
  /** Valores distintos no vacíos, por orden alfabético, para el filtro del listado (§8). */
  puestos?: string[];
  departamentos?: string[];
}

export const leerCatalogosEmpleados = async (): Promise<EmpleadosCatalogos> =>
  (await api.get<EmpleadosCatalogos>('/api/empleados/catalogos')).data;

// ───────────────────────────── Escrituras (Empleados.Gestionar)

/**
 * Campos profesionales de §2 comunes al alta y a la edición. En la edición es una sustitución total:
 * lo opcional que va a `null` se vacía en Gescom, y `nivelSeguridad` es obligatorio.
 */
export interface EmpleadoDatos {
  codigoCorto: string | null;
  nombreCompleto: string;
  nombreAbreviado: string | null;
  puesto: string | null;
  departamento: string | null;
  telefono: string | null;
  emailEmpresarial: string;
  idDelegacion: number;
  idZona: number | null;
  nivelSeguridad: number;
  nivelAsesor: number | null;
  nivelInterlocucion: number | null;
  accesoGdgo: boolean;
  asignarCcl: boolean;
  controlAcompaniamiento: boolean;
}

export interface EmpleadoAlta extends EmpleadoDatos {
  /** yyyy-MM-dd; sin valor, hoy. */
  fechaAlta?: string | null;
  roles?: string[];
}

export interface EmpleadoAltaResultado {
  idEmpleado: number;
  /** false: ya había un activo con ese email y no se ha creado otro. */
  creado: boolean;
}

/** `POST api/empleados`: 201 con `creado: true`, o 200 con `creado: false` si ya había un activo con ese email. */
export const crearEmpleado = async (alta: EmpleadoAlta): Promise<EmpleadoAltaResultado> =>
  (await api.post<EmpleadoAltaResultado>('/api/empleados', alta)).data;

/** `PUT api/empleados/{id}`: siempre 204, sin cuerpo. */
export const editarEmpleado = async (id: number, datos: EmpleadoDatos): Promise<void> => {
  await api.put(`/api/empleados/${id}`, datos);
};

export const guardarRolesEmpleado = async (id: number, roles: string[]): Promise<void> => {
  await api.put(`/api/empleados/${id}/roles`, { roles });
};

/** Baja lógica en la fecha indicada (yyyy-MM-dd). */
export const darDeBajaEmpleado = async (id: number, fechaBaja: string): Promise<void> => {
  await api.post(`/api/empleados/${id}/baja`, { fechaBaja });
};


// ───────────────────────────── Estado y clave de informes (CONTRATO.md §10, D-EMP-20)

/** `GET api/empleados/{id}/estado`. La clave de informes nunca viaja: solo se sabe si existe. */
export interface EstadoEmpleadoLeido {
  activo: boolean;
  fichaActiva: boolean;
  /** yyyy-MM-dd; sin valor si no hay baja. */
  fechaBaja: string | null;
  tieneClaveInformes: boolean;
}

/** Cuerpo de `PUT api/empleados/{id}/estado`. Activo no con ficha activa sí no se admite (422). */
export interface EstadoEmpleadoCambio {
  activo: boolean;
  fichaActiva: boolean;
  /** yyyy-MM-dd, no futura; solo con activo no. */
  fechaBaja?: string | null;
}

// Se copian campo a campo: lo que el servidor añada de más no llega a la pantalla.
export const leerEstadoEmpleado = async (id: number): Promise<EstadoEmpleadoLeido> => {
  const d = (await api.get<EstadoEmpleadoLeido>(`/api/empleados/${id}/estado`)).data;
  return { activo: d.activo, fichaActiva: d.fichaActiva, fechaBaja: d.fechaBaja ?? null, tieneClaveInformes: d.tieneClaveInformes === true };
};

/** `PUT api/empleados/{id}/estado`: 204, sin cuerpo. */
export const cambiarEstadoEmpleado = async (id: number, estado: EstadoEmpleadoCambio): Promise<void> => {
  await api.put(`/api/empleados/${id}/estado`, estado);
};

/**
 * `GET api/empleados/{id}/clave-informes` (Empleados.Gestionar, respuesta no-store): la clave, o `null` si no tiene.
 * Solo se copia `clave`. Quien la pide la guarda en memoria el tiempo justo y la descarta: nunca en almacenamiento, URL ni consola.
 */
export const leerClaveInformesEmpleado = async (id: number): Promise<string | null> => {
  const d = (await api.get<{ clave?: string | null }>(`/api/empleados/${id}/clave-informes`)).data;
  return typeof d?.clave === 'string' && d.clave !== '' ? d.clave : null;
};

// ───────────────────────────── Visibilidad a mano (CONTRATO.md §9, F-40 y F-41)

/** Un asesor que ve el empleado. `propia` es su fila consigo mismo: no se quita. */
export interface AsesorVisible { idAsesor: number; nombre?: string | null; activo: boolean; propia: boolean }
/** Fila del histórico de subregiones; abierta si `hasta` no tiene valor. Fechas yyyy-MM-dd. */
export interface SubregionEmpleado { idEmpSubregion: number; idSubregion: number; subregion?: string | null; desde?: string | null; hasta?: string | null }
export interface SubregionCatalogo { idSubregion: number; nombre: string }

export interface EmpleadoVisibilidad {
  asesores: AsesorVisible[];
  subregiones: SubregionEmpleado[];
  catalogoSubregiones: SubregionCatalogo[];
}

export interface CopiaVisibilidad { idOrigen: number; asesores: boolean; subregiones: boolean }
export interface CopiaVisibilidadResultado { asesores: number; subregionesAbiertas: number }

/** `GET api/empleados/{id}/visibilidad` (Empleados.Ver). */
export const leerVisibilidadEmpleado = async (id: number): Promise<EmpleadoVisibilidad> => {
  const d = (await api.get<Partial<EmpleadoVisibilidad>>(`/api/empleados/${id}/visibilidad`)).data;
  return { asesores: d?.asesores ?? [], subregiones: d?.subregiones ?? [], catalogoSubregiones: d?.catalogoSubregiones ?? [] };
};

/** `PUT api/empleados/{id}/visibilidad/asesores`: sustituye el conjunto de asesores. 204. */
export const guardarAsesoresVisibles = async (id: number, idAsesores: number[]): Promise<void> => {
  await api.put(`/api/empleados/${id}/visibilidad/asesores`, { idAsesores });
};

/** `POST api/empleados/{id}/visibilidad/copiar`: 200 con lo que ha cambiado. */
export const copiarVisibilidadEmpleado = async (id: number, copia: CopiaVisibilidad): Promise<CopiaVisibilidadResultado> => {
  const d = (await api.post<Partial<CopiaVisibilidadResultado>>(`/api/empleados/${id}/visibilidad/copiar`, copia)).data;
  return { asesores: d?.asesores ?? 0, subregionesAbiertas: d?.subregionesAbiertas ?? 0 };
};

/** `POST api/empleados/{id}/visibilidad/subregiones`: abre una subregión (`desde` yyyy-MM-dd, por defecto hoy). */
export const abrirSubregionEmpleado = async (id: number, idSubregion: number, desde?: string): Promise<void> => {
  await api.post(`/api/empleados/${id}/visibilidad/subregiones`, desde ? { idSubregion, desde } : { idSubregion });
};

/** `PUT api/empleados/{id}/visibilidad/subregiones/{idEmpSubregion}/cerrar`: pone la fecha de fin. 204. */
export const cerrarSubregionEmpleado = async (id: number, idEmpSubregion: number, hasta: string): Promise<void> => {
  await api.put(`/api/empleados/${id}/visibilidad/subregiones/${idEmpSubregion}/cerrar`, { hasta });
};
